import React from 'react'
import Image from 'gatsby-image'

import { UnderlineSectionHeader } from 'components/UnderlineText'
import InterpolateElement from 'components/utils/InterpolateElement'
import Price from 'components/price'
import { PrimaryButtonLink } from 'components/Buttons'
import checkMarkGreen from 'assets/images/icons/checkmark-green.svg'
import { getFrequency, getRenewalPeriod } from 'utils/payment'

import {
  container,
  planBgImageContainer,
  planBgImage,
  boxContainer,
  box,
  title as titleStyle,
  titleVariable,
  sellingPointsParentContainer,
  sellingPointTitle,
  sellingPointContainer,
  greenCheckmarkImage,
  description as descriptionStyle,
  priceHeader,
  buttonContainer,
} from './index.module.scss'

export interface PricePlanSectionQueryType {
  title: string
  sellingPoints: string[]
  description: { description: string }
  buttonText: string
  leftBackgroundImage: EF.FixedImageNode
  rightBackgroundImage: EF.FixedImageNode
  isBigPriceHidden: boolean
}

export interface PricePlanSectionProps {
  title: string
  discountPercentage?: number
  displayPrice?: number
  plan?: EF.GatsbySource8fitBackendPlan
  sellingPoints: string[]
  description: string
  buttonText: string
  signupPath: string
  leftBackgroundImage: EF.FixedImageNode
  rightBackgroundImage: EF.FixedImageNode
  isBigPriceHidden: boolean
}

const PricePlanSection = ({
  title,
  discountPercentage,
  displayPrice,
  plan,
  sellingPoints,
  description,
  buttonText,
  signupPath,
  leftBackgroundImage,
  rightBackgroundImage,
  isBigPriceHidden,
}: PricePlanSectionProps) => {
  let titleInterpolationParams = {}
  let descriptionInterpolationParams = {}
  let pricePeriod = ''
  const hasDiscount = discountPercentage !== undefined
  if (hasDiscount) {
    titleInterpolationParams = {
      percent_off: (
        <UnderlineSectionHeader
          text={`${discountPercentage}%`}
          className={titleVariable}
          tagName="span"
        />
      ),
    }
  }
  if (plan) {
    pricePeriod = getFrequency()[plan.period_name]

    const normalPrice = (
      <strong data-testid="normalPlanPrice">
        <Price
          value={plan.additionalInfo.normalizedPrice || 0}
          currencyCode={plan.device_currency}
        />
        /{pricePeriod}
      </strong>
    )

    descriptionInterpolationParams = {
      normal_price: normalPrice,
      discounted_price: hasDiscount ? (
        <strong data-testid="discountedPlanPrice">
          <Price
            value={displayPrice || 0}
            currencyCode={plan.device_currency}
          />
          /{pricePeriod}
        </strong>
      ) : (
        normalPrice
      ),
      plan_period: getRenewalPeriod()[plan.period_name],
      percent_off: `${hasDiscount ? discountPercentage : 0}%`,
    }
  }

  return (
    <section className={container}>
      <div className={planBgImageContainer}>
        <div className={planBgImage}>
          <Image fixed={leftBackgroundImage.fields.asset.fixed} alt="" />
        </div>
        <div className={planBgImage}>
          <Image fixed={rightBackgroundImage.fields.asset.fixed} alt="" />
        </div>
      </div>
      <div className={boxContainer}>
        <div className={box}>
          <div className={titleStyle} data-testid="pricePlanSectionTitle">
            <InterpolateElement
              text={title}
              parameters={titleInterpolationParams}
            />
          </div>
          <ul className={sellingPointsParentContainer}>
            {sellingPoints.map((point) => (
              <li className={sellingPointContainer} key={point}>
                <img
                  src={checkMarkGreen}
                  className={greenCheckmarkImage}
                  alt=""
                  role="presentation"
                />
                <span className={sellingPointTitle}>{point}</span>
              </li>
            ))}
          </ul>
          <p className={descriptionStyle}>
            <InterpolateElement
              text={description}
              parameters={descriptionInterpolationParams}
            />
          </p>
          {plan && displayPrice !== undefined && !isBigPriceHidden && (
            <span className={priceHeader}>
              <Price
                value={displayPrice || 0}
                currencyCode={plan.device_currency}
              />
              /{pricePeriod}
            </span>
          )}

          <div className={buttonContainer}>
            <PrimaryButtonLink text={buttonText} path={signupPath} />
          </div>
        </div>
      </div>
    </section>
  )
}

export default PricePlanSection
